import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiPlus } from 'react-icons/fi';
import { BACKEND_MAIN_URL } from '../config';
import { projectAuthHeaders } from '../projectAuth';
import { useOfflineSession } from '../offline/OfflineSessionContext';
import { addNote, acknowledgeNote, getProject, mergeServerNotes } from '../offline/projectRepository';

export default function ProjectNotes({ projectId, canEdit, onUseNote }) {
  const { t, i18n } = useTranslation('workspace');
  const { userId, offlineReadOnly, chatBackendUnavailable } = useOfflineSession();
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const online = !offlineReadOnly && !chatBackendUnavailable && navigator.onLine !== false;
  const refreshLocal = useCallback(async () => {
    if (!userId) return;
    const row = await getProject(userId, projectId);
    setNotes(row?.notes || []);
  }, [userId, projectId]);

  const synchronize = useCallback(async () => {
    if (!userId || offlineReadOnly || chatBackendUnavailable || navigator.onLine === false) return;
    try {
      const { data } = await axios.get(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(projectId)}/notes`, { headers: projectAuthHeaders() });
      await mergeServerNotes(userId, projectId, data.notes || []);
      const row = await getProject(userId, projectId);
      for (const note of (row?.notes || []).filter((item) => item.pending)) {
        const response = await axios.put(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(projectId)}/notes/${encodeURIComponent(note.id)}`,
          { text: note.text }, { headers: projectAuthHeaders() });
        await acknowledgeNote(userId, projectId, response.data);
      }
      await refreshLocal();
      setError('');
    } catch {
      await refreshLocal();
      setError('noteSyncError');
    }
  }, [userId, projectId, offlineReadOnly, chatBackendUnavailable, refreshLocal]);

  useEffect(() => {
    refreshLocal().catch(() => setError('noteStorageError'));
    synchronize();
  }, [refreshLocal, synchronize]);
  useEffect(() => {
    const onOnline = () => synchronize();
    const onVisible = () => { if (document.visibilityState === 'visible') synchronize(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener('online', onOnline); document.removeEventListener('visibilitychange', onVisible); };
  }, [synchronize]);

  async function save(event) {
    event.preventDefault();
    if (!canEdit || busy || !draft.trim()) return;
    setBusy(true); setError('');
    try {
      await addNote(userId, projectId, draft);
      setDraft('');
      await refreshLocal();
      await synchronize();
    } catch { setError('noteStorageError'); }
    finally { setBusy(false); }
  }
  return <div className="ws-notes">
    <div className="ws-section-heading"><h2>{t('fieldNotes')}</h2></div>
    <p className="ws-muted">{t('fieldNotesHint')}</p>
    {canEdit && <form className="ws-note-form" onSubmit={save}>
      <label className="ws-sr-only" htmlFor="field-note-draft">{t('newFieldNote')}</label>
      <textarea id="field-note-draft" rows={4} maxLength={5000} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={t('fieldNotePlaceholder')} />
      <button type="submit" className="ws-primary" disabled={busy || !draft.trim()}><FiPlus />{t('saveFieldNote')}</button>
    </form>}
    {error && <p role="alert" className="ws-error">{t(error)}{' '}<button type="button" className="ws-text-button" onClick={synchronize}>{t('retry')}</button></p>}
    {notes.length === 0 ? <p className="ws-muted">{t('noFieldNotes')}</p> : <ul className="ws-note-list">{[...notes].reverse().map((note) =>
      <li key={note.id}><p>{note.text}</p><small>{new Date(note.created_at).toLocaleString(i18n.resolvedLanguage || 'fr')}{note.pending && ` · ${t('notePending')}`}</small>{online && !note.pending && onUseNote && <button type="button" className="ws-text-button" onClick={() => onUseNote(note)}>{t('useFieldNote')}</button>}</li>)}</ul>}
    {!online && <p role="status" className="ws-muted">{t('notesOffline')}</p>}
  </div>;
}
